import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { CalendarDots, CaretLeft, CaretRight, Clock, MapPin, NavigationArrow } from '@phosphor-icons/react';
import { format, addWeeks, subWeeks, startOfWeek, addDays, isToday, isSameDay } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MyPlanningPage = () => {
  const { user } = useAuth();
  const [currentWeekStart, setCurrentWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weeksToShow, setWeeksToShow] = useState(2);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const start = format(currentWeekStart, 'yyyy-MM-dd');
      const endDate = addWeeks(currentWeekStart, weeksToShow);
      const end = format(addDays(endDate, -1), 'yyyy-MM-dd');
      const res = await axios.get(`${API_URL}/api/planning?start_date=${start}&end_date=${end}`, { withCredentials: true });
      setTasks(res.data);
    } catch (e) {
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [currentWeekStart, weeksToShow]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const goBack = () => setCurrentWeekStart(prev => subWeeks(prev, weeksToShow));
  const goForward = () => setCurrentWeekStart(prev => addWeeks(prev, weeksToShow));
  const goToday = () => setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));

  const allDays = [];
  for (let w = 0; w < weeksToShow; w++) {
    for (let d = 0; d < 7; d++) {
      allDays.push(addDays(addWeeks(currentWeekStart, w), d));
    }
  }

  const endLabel = format(addDays(addWeeks(currentWeekStart, weeksToShow), -1), 'd MMMM', { locale: fr });

  return (
    <div data-testid="my-planning-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <CalendarDots size={28} weight="bold" /> Mon Planning
          </h1>
          <p className="page-subtitle">
            {format(currentWeekStart, 'd MMMM', { locale: fr })} — {endLabel} {format(currentWeekStart, 'yyyy')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <select
            className="form-input"
            value={weeksToShow}
            onChange={(e) => setWeeksToShow(Number(e.target.value))}
            style={{ width: 'auto', fontSize: '0.85rem' }}
            data-testid="weeks-selector"
          >
            <option value={1}>1 semaine</option>
            <option value={2}>2 semaines</option>
            <option value={3}>3 semaines</option>
            <option value={4}>4 semaines</option>
          </select>
          <button className="btn btn-secondary btn-sm" onClick={goBack} data-testid="prev-weeks"><CaretLeft size={18} /></button>
          <button className="btn btn-primary btn-sm" onClick={goToday} data-testid="go-today">Aujourd'hui</button>
          <button className="btn btn-secondary btn-sm" onClick={goForward} data-testid="next-weeks"><CaretRight size={18} /></button>
        </div>
      </div>

      {loading ? (
        <div className="loading-container"><div className="spinner" /></div>
      ) : (
        <div style={{ display: 'grid', gap: '8px' }}>
          {allDays.map((day) => {
            const dayStr = format(day, 'yyyy-MM-dd');
            const dayTasks = tasks.filter(t => t.date === dayStr);
            const today = isToday(day);
            const isWeekend = day.getDay() === 0 || day.getDay() === 6;

            return (
              <div
                key={dayStr}
                data-testid={`day-${dayStr}`}
                className="card"
                style={{
                  padding: '0',
                  borderLeft: today ? '4px solid var(--primary, #002FA7)' : '4px solid transparent',
                  opacity: isWeekend && dayTasks.length === 0 ? 0.5 : 1,
                  backgroundColor: today ? '#FAFBFF' : 'white',
                }}
              >
                <div style={{
                  padding: '12px 16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom: dayTasks.length > 0 ? '1px solid #E2E2E6' : 'none',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: '40px', height: '40px', borderRadius: '8px',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      backgroundColor: today ? 'var(--primary, #002FA7)' : '#F4F5F7',
                      color: today ? 'white' : '#0A0B10',
                      fontWeight: '700', fontSize: '0.85rem', lineHeight: '1.1',
                    }}>
                      <span style={{ fontSize: '0.65rem', textTransform: 'uppercase' }}>
                        {format(day, 'EEE', { locale: fr })}
                      </span>
                      {format(day, 'd')}
                    </div>
                    <span style={{ fontSize: '0.9rem', fontWeight: today ? '700' : '400', color: today ? 'var(--primary, #002FA7)' : '#5E5F66' }}>
                      {format(day, 'EEEE d MMMM', { locale: fr })}
                    </span>
                  </div>
                  {dayTasks.length > 0 && (
                    <span style={{
                      backgroundColor: 'var(--primary, #002FA7)', color: 'white',
                      borderRadius: '12px', padding: '2px 10px', fontSize: '0.75rem', fontWeight: '700',
                    }}>
                      {dayTasks.length} tache{dayTasks.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>

                {dayTasks.length > 0 && (
                  <div style={{ padding: '8px 16px 12px' }}>
                    {dayTasks.map((task) => (
                      <div
                        key={task.id}
                        data-testid={`task-${task.id}`}
                        style={{
                          padding: '10px 14px', marginBottom: '6px',
                          borderRadius: '6px',
                          backgroundColor: task.is_leave ? '#FFF3E0' : '#F4F5F7',
                          borderLeft: `3px solid ${task.is_leave ? '#EF6C00' : 'var(--primary, #002FA7)'}`,
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                          <span style={{ fontWeight: '700', fontSize: '0.9rem', color: '#0A0B10' }}>
                            {task.title}
                          </span>
                          <span style={{
                            display: 'flex', alignItems: 'center', gap: '4px',
                            fontSize: '0.8rem', color: '#5E5F66', fontWeight: '600',
                          }}>
                            <Clock size={14} /> {task.start_time} - {task.end_time}
                          </span>
                        </div>
                        {task.description && (
                          <p style={{ fontSize: '0.8rem', color: '#5E5F66', margin: '4px 0 0' }}>{task.description}</p>
                        )}
                        {task.address && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px' }}>
                            <MapPin size={14} style={{ color: '#5E5F66' }} />
                            <span style={{ fontSize: '0.78rem', color: '#5E5F66' }}>{task.address}</span>
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(task.address)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: 'var(--primary, #002FA7)', fontSize: '0.75rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '3px' }}
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`navigate-${task.id}`}
                            >
                              <NavigationArrow size={12} /> Itineraire
                            </a>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {dayTasks.length === 0 && !isWeekend && (
                  <div style={{ padding: '0 16px 12px', fontSize: '0.8rem', color: '#9E9EA7', fontStyle: 'italic' }}>
                    Aucune tache planifiee
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyPlanningPage;
